import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronRight, Folder, FolderPlus, GitBranch, Home, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import type { ProjectBrowse, ProjectDirectory } from "@/lib/types";
import { cn } from "@/lib/utils";

const FOLDER_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;

/**
 * Picks a project folder from the folders mounted into the board (`PROJECT_ROOTS`),
 * or makes a new one there — the way a project usually starts is an empty folder.
 *
 * Browsing is the server's (it refuses anything outside a root, so this cannot
 * walk the machine); this is breadcrumbs over it, a filter for a folder with a lot
 * in it, "use this folder" for the one you have navigated into, and an inline
 * "new folder" that creates it and picks it in one go. Renders nothing when the
 * board runs without mounted roots — then the path field is the whole story.
 */
export function FolderPicker({ selected, onPick }: { selected: string; onPick: (entry: ProjectDirectory) => void }) {
  const [browse, setBrowse] = useState<ProjectBrowse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const nameBox = useRef<HTMLInputElement>(null);

  const open = useCallback(async (path?: string) => {
    setLoading(true);
    setError(null);
    setFilter("");
    try {
      setBrowse(await api.browseProjects(path));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not list that folder");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void open();
  }, [open]);

  useEffect(() => {
    if (naming) nameBox.current?.focus();
  }, [naming]);

  /** Root → here, each step clickable. */
  const crumbs = useMemo(() => {
    if (!browse?.path) return [];
    const root = browse.roots.find((candidate) => browse.path === candidate.path || browse.path!.startsWith(`${candidate.path}/`));
    if (!root) return [];
    const rest = browse.path.slice(root.path.length).split("/").filter(Boolean);
    const steps = [{ label: root.hostPath.replace(/^\/home\/[^/]+/, "~"), path: root.path }];
    let cursor = root.path;
    for (const part of rest) {
      cursor = `${cursor}/${part}`;
      steps.push({ label: part, path: cursor });
    }
    return steps;
  }, [browse]);

  const entries = useMemo(() => {
    const list = browse?.entries ?? [];
    const needle = filter.trim().toLowerCase();
    return needle ? list.filter((entry) => entry.name.toLowerCase().includes(needle)) : list;
  }, [browse, filter]);

  if (!browse || browse.roots.length === 0) return error ? <p className="text-xs text-destructive">{error}</p> : null;

  const here = browse.path;
  const hereIsProject = here !== null && browse.entries.length >= 0;
  const validName = FOLDER_NAME.test(name.trim());
  const clash = browse.entries.some((entry) => entry.name === name.trim());

  const create = async () => {
    if (!here || !validName || clash) return;
    setCreating(true);
    setError(null);
    try {
      const made = await api.createProjectDirectory(here, name.trim());
      setNaming(false);
      setName("");
      await open(here);
      onPick(made);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not create the folder");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label hint="mounted into the board">Pick or create a folder</Label>
      </div>

      <div className="overflow-hidden rounded-md border border-border/60">
        {/* Breadcrumbs, and the actions for the folder you are in. */}
        <div className="flex flex-wrap items-center gap-1 border-b border-border/60 bg-muted/30 px-2 py-1.5">
          {here === null ? (
            <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
              <Home className="size-3.5" /> Mounted folders
            </span>
          ) : (
            <nav className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5 font-mono text-[11.5px]" aria-label="Folder path">
              {browse.roots.length > 1 ? (
                <button type="button" className="rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => void open()}>
                  <Home className="size-3" />
                </button>
              ) : null}
              {crumbs.map((crumb, index) => (
                <span key={crumb.path} className="flex items-center gap-0.5">
                  {index > 0 || browse.roots.length > 1 ? <ChevronRight className="size-3 text-muted-foreground/60" /> : null}
                  <button
                    type="button"
                    onClick={() => void open(crumb.path)}
                    className={cn(
                      "max-w-40 truncate rounded px-1 py-0.5 hover:bg-muted",
                      index === crumbs.length - 1 ? "font-semibold text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {crumb.label}
                  </button>
                </span>
              ))}
            </nav>
          )}
          {here !== null ? (
            <Button type="button" variant="ghost" size="xs" onClick={() => setNaming((value) => !value)} aria-expanded={naming}>
              <FolderPlus /> New folder
            </Button>
          ) : null}
        </div>

        {naming && here !== null ? (
          <div className="flex items-center gap-1.5 border-b border-border/60 px-2 py-1.5">
            <FolderPlus className="size-3.5 shrink-0 text-primary" />
            <Input
              ref={nameBox}
              value={name}
              placeholder="new-project"
              className="h-7 font-mono text-[12px]"
              onChange={(event) => setName(event.target.value.replace(/\s+/g, "-"))}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void create();
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setNaming(false);
                }
              }}
            />
            <Button type="button" size="xs" loading={creating} disabled={!validName || clash} onClick={() => void create()}>
              Create
            </Button>
            <Button type="button" variant="ghost" size="icon-sm" aria-label="Cancel" onClick={() => setNaming(false)}>
              <X />
            </Button>
          </div>
        ) : null}
        {naming && name.trim() && (!validName || clash) ? (
          <p className="border-b border-border/60 px-2.5 py-1 text-[11px] text-destructive">
            {clash ? "There is already a folder with that name here." : "Letters, numbers, dots, dashes and underscores; start with a letter or number."}
          </p>
        ) : null}

        {here !== null && browse.entries.length > 8 ? (
          <div className="relative border-b border-border/60">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={`Filter ${browse.entries.length} folders`}
              className="h-8 w-full bg-transparent pl-8 pr-2 text-[12.5px] outline-none placeholder:text-muted-foreground"
            />
          </div>
        ) : null}

        <div className="max-h-52 overflow-y-auto scrollbar-slim">
          {(here === null
            ? browse.roots.map((root) => ({ name: root.hostPath, path: root.path, hostPath: root.hostPath, projectId: null, isRepo: false }))
            : entries
          ).map((entry) => (
            <div
              key={entry.path}
              className={cn("flex items-center gap-2 px-2.5 py-1 text-[12.5px] hover:bg-muted/50", selected === entry.hostPath && "bg-primary/10")}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 py-0.5 text-left"
                onClick={() => void open(entry.path)}
                title="Open this folder"
              >
                {entry.isRepo ? (
                  <GitBranch className="size-3.5 shrink-0 text-primary" />
                ) : (
                  <Folder className="size-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate">{entry.name}</span>
              </button>
              {entry.projectId ? (
                <Badge variant="outline">registered</Badge>
              ) : here !== null ? (
                selected === entry.hostPath ? (
                  <span className="flex items-center gap-1 text-[11.5px] text-primary">
                    <Check className="size-3.5" /> picked
                  </span>
                ) : (
                  <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[11.5px]" onClick={() => onPick(entry)}>
                    Use
                  </Button>
                )
              ) : null}
            </div>
          ))}
          {here !== null && entries.length === 0 && !loading ? (
            <p className="px-2.5 py-2 text-[12px] text-muted-foreground">
              {filter ? "No folder matches." : "No folders in here — use this one, or make a new one."}
            </p>
          ) : null}
          {loading ? <p className="px-2.5 py-2 text-[12px] text-muted-foreground">Loading…</p> : null}
        </div>

        {/* The folder you have navigated into is a candidate too, not only its children. */}
        {hereIsProject && here !== null && crumbs.length > 1 ? (
          <div className="flex items-center gap-2 border-t border-border/60 bg-muted/20 px-2.5 py-1.5 text-[12px]">
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              This folder: <span className="font-mono text-foreground">{crumbs[crumbs.length - 1]!.label}</span>
            </span>
            {browse.hostPath && selected === browse.hostPath ? (
              <span className="flex items-center gap-1 text-[11.5px] text-primary">
                <Check className="size-3.5" /> picked
              </span>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() =>
                  onPick({
                    name: crumbs[crumbs.length - 1]!.label,
                    path: here,
                    hostPath: browse.hostPath ?? here,
                    projectId: null,
                    isRepo: false,
                  })
                }
              >
                Use this folder
              </Button>
            )}
          </div>
        ) : null}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
